import React, { useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridFilterModel,
  GridSortModel,
  getGridDateOperators,
} from '@mui/x-data-grid-pro';
import IconButton from '@mui/material/IconButton';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';

import { CompanyCertification } from '../../interfaces/users.interface';
import GridPaginationBar from './GridPaginationBar';
import {
  getCompanyCertificationsForDataGrid,
  updateCompanyCertificationApproval,
  updateCompanyCertificatioReviewed,
} from '../../services/companyCertifications';
import { Card, Switch } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import { Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { USER_TYPES } from '../../services/user';
import { formatDate, normalizeFilterModel, stringContainsOnly } from '../../utils/adminDashboardUtils';

interface CertificationRow {
  id: number | undefined;
  companyName: string | undefined;
  companyId: number | undefined;
  expirationDate?: string | Date;
  adminApproved: boolean;
  adminReviewed: boolean;
  certification: string;
  version: string;
  releaseDate?: string | Date;
  document: string;
  createdAt?: string | Date;
}

function createRowLink(certRow: CertificationRow) {
  return (
    <a href={certRow.document} target="_blank" rel="noreferrer">
      <IconButton size="small" name="View Certification">
        <LinkOutlinedIcon />
      </IconButton>
    </a>
  );
}

function createRowSwitch(certRow: CertificationRow) {
  const { user } = useAppSelector(state => state.user);
  const userIsAdmin: boolean = user?.type ? user.type === USER_TYPES.TPN_ADMIN : false;
  const companyCertId = `cert-${certRow.id}`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!certRow.id) return;
    updateCompanyCertificationApproval(certRow.id, event.target.checked);
  };

  return (
    <Switch
      id={companyCertId}
      disabled={!userIsAdmin}
      defaultChecked={certRow.adminApproved}
      onChange={handleChange}
      name={companyCertId}
    />
  );
}

function createReviewedRowSwitch(certRow: CertificationRow) {
  const { user } = useAppSelector(state => state.user);
  const userIsAdmin: boolean = user?.type ? user.type === USER_TYPES.TPN_ADMIN : false;
  const companyCertId = `cert-${certRow.id}`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!certRow.id) return;
    updateCompanyCertificatioReviewed(certRow.id, event.target.checked);
  };

  return (
    <Switch
      id={companyCertId}
      disabled={!userIsAdmin}
      defaultChecked={certRow.adminReviewed}
      onChange={handleChange}
      name={companyCertId}
    />
  );
}

const columns: GridColDef[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 70,
    filterOperators: stringContainsOnly,
  },
  {
    field: 'company__name',
    headerName: 'Company Name',
    filterOperators: stringContainsOnly,
    width: 230,
    renderCell: params => (
      <Link to={`/registry/${params.row.companyId}`}>
        {params.row.companyName}
      </Link>
    ),
  },
  {
    field: 'certification__title',
    headerName: 'Certification',
    filterOperators: stringContainsOnly,
    width: 200,
    valueGetter: params => params.row.certification,
    valueFormatter: params => params.value,

  },
  {
    field: 'certification__version',
    headerName: 'Version',
    filterOperators: stringContainsOnly,
    width: 130,
    valueGetter: params => params.row.version,
    valueFormatter: params => params.value,
  },
  {
    field: 'created_at',
    headerName: 'Upload Date',
    type: 'dateTime',
    filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
    width: 130,
    valueGetter: params => formatDate(params.row.createdAt),
    renderCell: params => formatDate(params.row.createdAt),
  },
  {
    field: 'certification__release_date',
    headerName: 'Release Date',
    type: 'dateTime',
    filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
    width: 130,
    valueGetter: params => formatDate(params.row.releaseDate),
    renderCell: params => formatDate(params.row.releaseDate),
  },
  {
    field: 'expiration_date',
    headerName: 'Expiration Date',
    type: 'dateTime',
    filterOperators: getGridDateOperators().filter(op => op.value === 'is'),
    width: 130,
    valueGetter: params => formatDate(params.row.expirationDate),
    renderCell: params => formatDate(params.row.expirationDate),
  },
  {
    field: 'adminApproved',
    headerName: 'Admin Approved',
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: params => createRowSwitch(params.row),
  },
  {
    field: 'adminReviewed',
    headerName: 'Admin Reviewed',
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: params => createReviewedRowSwitch(params.row),
  },
  {
    field: 'link',
    headerName: 'Link',
    width: 130,
    sortable: false,
    filterable: false,
    renderCell: params => createRowLink(params.row),
  },
];

function createCertificationRow(companyCert: CompanyCertification): CertificationRow {
  return {
    id: companyCert.id,
    companyId: companyCert.company,
    companyName: companyCert.companyName,
    expirationDate: companyCert.expirationDate,
    adminApproved: !!companyCert.adminApproved,
    adminReviewed: !!companyCert.adminReviewed,
    certification: companyCert.certification.title,
    version: companyCert.certification.version || '-',
    releaseDate: companyCert.certification.releaseDate,
    document: companyCert.document,
    createdAt: companyCert.createdAt,
  };
}

export default function CompanyCertificationAdminPanel() {
  const [rows, setRows] = useState<CertificationRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const fetchAndSetRows = async () => {
    try {
      const data = await getCompanyCertificationsForDataGrid({
        page,
        pageSize,
        sortModel,
        filterModel,
      });

      const mapped = data.results.map(createCertificationRow);
      setRows(mapped);
      setRowCount(data.count);
    } catch (err) {
      console.error('Error fetching company certifications', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetRows();
  }, [page, pageSize, sortModel, filterModel]);

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        style={{ height: 400, width: '100%' }}
        rows={rows}
        columns={columns}
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100, 200]}
        rowCount={rowCount}
        onPageChange={newPage => {
          setPage(newPage);
        }}
        onPageSizeChange={newPageSize => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={model => {
          setPage(0);
          setSortModel(model);
        }}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(model) => {
          const normalized = normalizeFilterModel(model);
          const prev = JSON.stringify(filterModel.items ?? []);
          const next = JSON.stringify(normalized.items ?? []);

          if (prev === next) return;

          setPage(0);
          setFilterModel(normalized);
        }}
        loading={isLoading}
        components={{
          Footer: GridPaginationBar,
          LoadingOverlay: () => (
            <GridOverlay>
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
      />
    </Card>
  );
}
